import { Params } from 'react-router-dom';

// Offline
import { addCompoundOperation } from '../../../../../../offline/offlineCompoundQueue';
import { captureLocalImages } from '../../../../../../offline/offlineImageStore';

// Utils
import { deleteWholeDirectory } from '../../../../../../utils/files';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../../../utils/sessionStorage';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
  directoryNaming,
} from '../../../../../../components/forms/ActionForms/constants';

// Types
import { OrderType } from '../../../../../../types/orders';

/**
 * Marca de la foto de la operación: se encoló con las imágenes YA resueltas
 * (`withImages: false`, el «flap» a mitad del guardado). Su reintento no lleva
 * carpetas y no tiene croquis que subir; sin la marca, el croquis apuntado por
 * nombre que no está en la copia (vacía) se avisaba a Sentry como perdido
 * (`sketch-missing:action`) sin serlo (Ruling 20). Las operaciones encoladas
 * antes no la llevan y se reintentan como siempre.
 */
export const VALIDATION_IMAGES_RESOLVED_SNAPSHOT = '_images_resolved';

interface QueueParams {
  formData: FormData;
  params?: Params<string>;
  /** Order de la actuación que se edita (padre de la compound) y su ROID. */
  orderId: string;
  orderRoid: string;
  /**
   * Id de la actuación. Ya NO decide el destino (ver abajo); se conserva
   * porque el llamador lo tiene a mano y documenta a quién pertenece la cola.
   */
  actionId: string;
  /**
   * Las fotos solo se capturan cuando NO se han resuelto todavía. Tras el
   * bloque de imágenes ya están subidas o en la cola de imágenes, y volver a
   * capturarlas del OPFS las duplicaría en el replay (`createImage` deduplica
   * por `clientOpId`, y el replay genera uno nuevo).
   */
  withImages: boolean;
  /**
   * El guardado en vivo ya entregó las fotos nuevas a la bandeja antes de
   * fallar. La operación las sigue llevando (el reintento ve el mismo
   * formulario), pero no las vuelve a entregar (spec
   * 2026-10-01-photo-outbox-identity § 10.4 E5).
   */
  photosHandedOff?: boolean;
}

/**
 * Captura el asistente de VALIDAR una actuación entero y lo encola como
 * operación compound `action-edit` (spec 2026-09-11 § 1 E4).
 *
 * Es el código que ya vivía duplicado en los dos caminos sin red del
 * `saveData`; ahora lo comparten esos dos y el camino nuevo del plazo vencido.
 * El replay (`syncActionEdit`) reconstruye el `FormData` iterando el prefijo
 * `_formData_`, así que la captura tiene que ser genérica.
 */
export async function queueValidationEdit({
  formData,
  params,
  orderId,
  orderRoid,
  actionId,
  withImages,
  photosHandedOff,
}: QueueParams): Promise<{ redirectTo: string }> {
  const sessionSnapshots: Record<string, string | null> = {};
  Object.values(formActionNaming).forEach((key) => {
    sessionSnapshots[key] = sessionStorage.getItem(key);
  });
  Object.values(formActionValidationNaming).forEach((key) => {
    sessionSnapshots[key] = sessionStorage.getItem(key);
  });
  // Los ~28 campos del FormData (actionPkInit, editionDescription, intent…).
  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string') sessionSnapshots[`_formData_${key}`] = value;
  }
  // params de ruta: el saveData cae a ellos cuando sessionStorage está vacío.
  sessionSnapshots['_params_id'] = params?.id ?? null;
  if (!withImages) sessionSnapshots[VALIDATION_IMAGES_RESOLVED_SNAPSHOT] = 'true';

  const localImages = withImages
    ? await captureLocalImages([directoryNaming.CANVAS, OrderType.Action])
    : [];

  await addCompoundOperation({
    type: 'action-edit',
    parentOrderId: orderId,
    parentOrderRoid: orderRoid,
    isCampaignParent: false,
    sessionSnapshots,
    localImages,
    ...(photosHandedOff && { photosHandedOff: true }),
  });

  // OPFS: o viaja capturado en `localImages`, o ya está resuelto. Limpiarlo
  // evita sobrantes que bloqueen (skip-if-exists) el próximo guardado con el
  // mismo nombre de fichero (GAP H3, cazado a mano el 2026-07-04).
  deleteWholeDirectory(directoryNaming.CANVAS);
  deleteWholeDirectory(OrderType.Action);

  // El contexto de navegación vive DENTRO de la misma clave que se va a
  // reescribir, así que hay que leerlo antes de limpiar (igual que el camino
  // con red): dice de dónde vino el operario y a dónde volver luego.
  let contextoNavegacion: Record<string, unknown> = {};
  const crudo = sessionStorage.getItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
  if (crudo) {
    try {
      const previo = JSON.parse(decompressSessionStorage(crudo));
      contextoNavegacion = {
        origin: previo.origin,
        parentOrderId: previo.parentOrderId,
        parentRoid: previo.parentRoid,
        entityRoid: previo.entityRoid,
        returnTo: previo.returnTo,
      };
    } catch {
      // borrador ilegible: se sigue sin contexto, no es motivo para fallar
    }
  }

  Object.values(formActionNaming).forEach((key) => sessionStorage.removeItem(key));
  Object.values(formActionValidationNaming).forEach((key) =>
    sessionStorage.removeItem(key)
  );

  // 🔴 La marca que el resumen usa para saber que vienes DEL ASISTENTE, la
  // misma que deja el camino con red antes de redirigir. Sin ella el loader
  // cae a `isActionEdited = 'false'` y pinta el PRIMER resumen: botones de
  // «Validar / Pendiente de certificar» como si no hubieras hecho nada, y
  // «Continuar» devolviéndote al asistente. El propio saveData ya avisa de
  // esta clase de fallo en su cabecera; encolar lo reintrodujo por la puerta
  // de atrás al limpiar el asistente entero (reportado en carretera).
  sessionStorage.setItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA,
    compressSessionStorage(
      JSON.stringify({
        id: actionId,
        orderId,
        roid: orderRoid,
        isActionEdited: 'true',
        ...contextoNavegacion,
      }),
    ),
  );

  // 🔴 Al RESUMEN, el mismo destino que con red (corregido el 2026-09-12).
  // El 11-sep lo cambié a la tabla porque el resumen enseña la actuación como
  // estaba ANTES —lee del servidor, que aún no tiene lo encolado—, pero eso
  // rompía algo más importante: **encolar no puede cambiar a dónde aterrizas**.
  // El mismo gesto acababa en pantallas distintas según la cobertura, y solo en
  // este flujo. Lo del resumen desactualizado es otra cosa y se trata aparte.
  return { redirectTo: `../../summary/${actionId}` };
}
